/**
 * The single money format for the whole app: 1,234,567.50
 * (thousands separators, always two decimals). Everything that shows or edits
 * an amount should go through these helpers or <MoneyInput />, so there is
 * one place to change it.
 */

const formatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/**
 * 7500000 -> "7,500,000.00". Missing/invalid values become "0.00", never "NaN".
 * `currency: true | 'NGN'` adds the naira sign; any other ISO code (e.g. 'USD')
 * uses that currency's own symbol, still with two decimals.
 */
export function formatMoney(value: unknown, opts: { currency?: boolean | string } = {}): string {
  const n = toNumber(value);
  const code = opts.currency === true ? 'NGN' : opts.currency || '';
  if (!code) return formatter.format(n);
  if (code === 'NGN') {
    const text = formatter.format(Math.abs(n));
    return `${n < 0 ? '-' : ''}₦${text}`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

/** "1,234,567.50" / "₦ 7,500,000" -> number; empty or non-numeric -> undefined. */
export function parseMoney(text: string): number | undefined {
  const cleaned = text.replace(/[₦,\s]/g, '');
  if (cleaned === '' || cleaned === '.' || cleaned === '-') return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Live formatting while typing: separators only, no forced decimals, letters
 * dropped, at most two decimal places ("12ab3" -> "123", "1234567." -> "1,234,567.").
 */
export function formatMoneyTyping(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  const intPart = (dot === -1 ? cleaned : cleaned.slice(0, dot)).replace(/^0+(?=\d)/, '');
  const decPart = dot === -1 ? null : cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  const grouped = (intPart === '' ? (decPart === null ? '' : '0') : intPart).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart === null ? grouped : `${grouped}.${decPart}`;
}
